import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { ActivePharmacyProvider } from '@/auth/active-pharmacy';
import { OfflineProvider } from '@/lib/offline/offline-context';
import { AuthProvider } from '@/auth/auth-context';
import { IS_DEV } from '@/lib/env';
// Side effect: catches `beforeinstallprompt`, which fires once and early -
// long before the settings screen that offers it is ever opened.
import '@/lib/install';
import { queryClient } from '@/lib/query-client';
import { listenForStaleBuild } from '@/lib/stale-build';
import { router } from '@/routes';
import { ThemeProvider } from '@/theme/theme-provider';

import './styles.css';

// Before anything renders: a tab that outlived a deploy reloads onto the new build.
listenForStaleBuild();

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root - check index.html');

/*
 * Provider order matters: AuthProvider issues queries, so it must sit inside
 * QueryClientProvider; ActivePharmacyProvider reads the caller's staff
 * assignment from /auth/me, so it sits inside AuthProvider.
 */
/*
 * The service worker exists so the app still LOADS with no network - nothing
 * more. It caches the shell and never touches `/api`; the data the till needs
 * offline is cached deliberately in IndexedDB by `lib/offline/`. See public/sw.js.
 *
 * Production only: in development it would sit between Vite and the browser and
 * serve yesterday's modules over HMR.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((error) => {
      // Not fatal: without it the app simply cannot cold-start offline.
      console.warn('Offline shell unavailable -', error);
    });
  });
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ActivePharmacyProvider>
            {/* Reads the active branch, so it sits inside it; wraps the router
                because the connection state is chrome, not a page. */}
            <OfflineProvider>
              <RouterProvider router={router} />
            </OfflineProvider>
          </ActivePharmacyProvider>
        </AuthProvider>
      </ThemeProvider>
      {IS_DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  </StrictMode>,
);
